import { SellerTable, Column } from "../../../components/sellerDashboardTable";
import { ProductStatusCard } from "../../../components/ProductStatusCard";
import { CategorySelector } from "../../../components/TableSelector";
import { FilterCategory, FilterDropdown } from "../../../components/TreeSelector";
import styles from "./style.module.css";
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Dropdown, Option } from "../../../components/DropdownMenu";
import { DeleteProductModal } from "../../../components/DeleteProductModal";
import Typography from "../../../components/Typography/Typography";
import { useNavigate } from "react-router-dom";
import { ProductListItem } from "../../../components/ProductListItem";
import { addBtn, exportIcon, filter, searchIcon } from "../../../assets/svgs";
import { useGetSellerProductsQuery } from "../../../redux/services/productApi";
import { AddProductModal } from "../../../components/AddProductModal/AddProductModal";
import type { RootState } from "../../../redux/store";

interface Order {
  productName: string;
  category: string;
  stock: number;
  price: number;
  status: string;
  menu?: React.ReactNode;
}

interface Product {
  id: string;
  name: string;
  description: string;
  category: string;
  image: string;
  quantity: number;
}

export const ProductList = () => {
  const [search, setSearch] = useState("");
  const [selectedBrand, setSelectedBrand] = useState<string>();
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openAddProductModal, setOpenAddProductModal] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [filterToggle, setfilterToggle] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();

  const selectedStoreId = useSelector((state: RootState) => state.selectedStore.storeId);

  const statusMap: { [key: string]: 'high_stock' | 'low_stock' | 'out_of_stock' | undefined } = {
    'all': undefined,
    'high-stock': 'high_stock',
    'low-stock': 'low_stock',
    'out-of-stock': 'out_of_stock',
  };

  const {
    data: productsData,
    isLoading,
    error,
    refetch,
  } = useGetSellerProductsQuery({
    status: statusMap[selectedCategory],
    searchTerm: search || undefined,
    limit: 50,
    currentPage: currentPage,
    ...(selectedStoreId !== 'all' && { store_id: selectedStoreId }),
  }, {
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    refetch();
  }, [selectedCategory, search, currentPage, selectedStoreId, refetch]);

  const handleAddProduct = (product: Product) => {
    setProducts(prevProducts => [...prevProducts, product]);
  };

  const handleAddModal = () => {
    setOpenAddProductModal(!openAddProductModal);
  };

  const handleDeleteModal = () => {
    setOpenDeleteModal(!openDeleteModal);
  };

  const handleViewing = (name: string) => {
    navigate(`/view/${name}`);
  };

  const handleEditing = (name: string) => {
    navigate(`/edit/${name}`);
  };

  const getStatusComponent = (status?: string) => {
    const map: { [key: string]: "High Stock" | "Low Stock" | "Out of Stock" } = {
      high_stock: "High Stock",
      low_stock: "Low Stock",
      out_of_stock: "Out of Stock",
    };

    const statusText: "High Stock" | "Low Stock" | "Out of Stock" | undefined = status
      ? map[status] ?? undefined
      : undefined;

    return <ProductStatusCard title={statusText} />;
  };

  const columns: Column<Order>[] = [
    { title: "PRODUCT NAME", key: "productName" },
    { title: "CATEGORY", key: "category" },
    { title: "STOCK", key: "stock" },
    { title: "PRICE", key: "price" },
    { title: "STATUS", key: "status" },
    { title: "", key: "menu" },
  ];

  const getOptions = (productName: string): Option[] => [
    {
      value: "view",
      label: (
        <div onClick={() => handleViewing(productName)}>
          <Typography>View</Typography>
        </div>
      ),
    },
    {
      value: "edit",
      label: (
        <div onClick={() => handleEditing(productName)}>
          <Typography>Edit</Typography>
        </div>
      ),
    },
    {
      value: "delete",
      label: (
        <div onClick={handleDeleteModal}>
          <Typography className={styles.deleteBtn}>Delete</Typography>
        </div>
      ),
    },
  ];

  const data: Order[] = productsData?.data.map((product) => {
    const prodAny = product as any;
    const computedStock =
      typeof prodAny.stock === "number"
        ? prodAny.stock
        : (typeof prodAny.amount === "number" && typeof prodAny.stock_sold === "number")
          ? Math.max(0, prodAny.amount - prodAny.stock_sold)
          : (prodAny.stock_sold ?? 0);

    const computedPrice = prodAny.price ?? prodAny.amount ?? 0;
    const computedCategory = Array.isArray(prodAny.category) ? prodAny.category.join(", ") : prodAny.category;

    return {
      productName: prodAny.name,
      category: computedCategory,
      stock: computedStock,
      price: computedPrice,
      status: (prodAny.status),
      menu: (
        <Dropdown
          options={getOptions(prodAny.name)}
          value={selectedBrand}
          onChange={setSelectedBrand}
        />
      ),
    };
  }) || [];

  const categories = [
    { id: "all", label: "All" },
    { id: "high-stock", label: "High stock" },
    { id: "low-stock", label: "Low stock" },
    { id: "out-of-stock", label: "Out of stock" },
  ];

  const categoriesFilter: FilterCategory[] = [
    {
      title: "Product Name",
      options: [
        { label: "Lenovo", count: 185 },
        { label: "Dell", count: 92 },
        { label: "HP", count: 120 },
      ],
    },
    {
      title: "Category",
      options: [
        { label: "Laptop", count: 210 },
        { label: "Desktop", count: 87 },
        { label: "Monitor", count: 60 },
      ],
    },
    {
      title: "Stock",
      options: [
        { label: "In Stock", count: 300 },
        { label: "Out of Stock", count: 40 },
      ],
    },
    {
      title: "Price",
      options: [
        { label: "Below $100", count: 50 },
        { label: "$100 - $300", count: 180 },
        { label: "Above $300", count: 70 },
      ],
    },
  ];

  const handleFilterChange = (selected: string[]) => {
    console.log("Selected Filters:", selected);
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <Typography variant="heading5" fontFamily="satoshi">
            Loading products...
          </Typography>
        </div>
      </div>
    );
  }

  if (error) {
    const errorMessage = 'error' in error
      ? error.error
      : 'status' in error
        ? `Error: ${error.status}`
        : 'Failed to load products';

    return (
      <div className={styles.container}>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <Typography
            variant="heading5"
            fontFamily="satoshi"
            className={styles.errorMessage}
          >
            Error Loading Products
          </Typography>
          <Typography variant="bodyRegular" fontFamily="satoshi">
            {errorMessage}
          </Typography>
          <button
            onClick={() => refetch()}
            style={{
              marginTop: '20px',
              padding: '10px 20px',
              background: '#1D1D1C',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontFamily: 'Satoshi',
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.tableTop}>
        <div className={styles.searchContainer}>
          <img src={searchIcon} alt="search icon" />
          <input
            type="text"
            placeholder='Search'
            value={search}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSearch(e.target.value)
            }
          />
        </div>

        <div className={styles.filterContainer}>
          <button
            title="Filter"
            aria-label="Filter products"
            className={styles.btnIcon}
            onClick={() => { setfilterToggle(!filterToggle) }}
          >
            <img src={filter} alt="filter icon" />
          </button>

          {filterToggle && categoriesFilter && (
            <div className={styles.filterDropdown}>
              <FilterDropdown
                categories={categoriesFilter}
                onChange={handleFilterChange}
                classname={styles.dropdownMenu}
              />
            </div>
          )}
        </div>

        <button title="export icon" className={styles.btnIcon}>
          <img src={exportIcon} alt="export icon" />
        </button>
      </div>

      <div className={styles.categoryContainer}>
        <Typography
          variant="heading3"
          fontFamily="satoshi"
          size="base"
        >
          <CategorySelector
            categories={categories}
            defaultSelected="all"
            onSelect={(id) => {
              console.log("Selected category:", id);
              setSelectedCategory(id);
            }}
          />
        </Typography>
      </div>

      {data.length > 0 ? (
        <>
          <div className={styles.tableContainer}>
            <SellerTable
              header={columns as Column<{ id?: string | number }>[]}
              data={data as { id?: string | number }[]}
              filters={categoriesFilter}
              searchFor={search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearch(e.target.value)
              }
              addModal={() => { handleAddModal() }}
              addBtn={true}
            />
          </div>

          <div className={styles.moblieView}>
            {data.map((item, index) => (
              <ProductListItem
                key={index}
                name={item.productName}
                category={item.category}
                price={item.price}
                status={item.status}
                onClick={() => {}}
              />
            ))}
          </div>
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <Typography variant="bodyRegular" fontFamily="satoshi">
            No products found. {search ? 'Try a different search term.' : 'Add your first product to get started.'}
          </Typography>
          <button
            onClick={() => handleAddModal()}
            style={{
              marginTop: '20px',
              padding: '12px 24px',
              background: '#1D1D1C',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontFamily: 'Satoshi',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            Add Product
          </button>
        </div>
      )}

      <button
        title="add button"
        className={styles.addBtnContainer}
        onClick={() => handleAddModal()}
      >
        <img src={addBtn} alt="add button" />
      </button>

      <DeleteProductModal
        open={openDeleteModal}
        toggle={() => { handleDeleteModal() }}
      />

      <AddProductModal
        isOpen={openAddProductModal}
        onClose={() => { handleAddModal() }}
        onAddProduct={handleAddProduct}
        productIndex={null}
        storeId={selectedStoreId}
      >
        Add Product
      </AddProductModal>
    </div>
  );
};